import $ from 'jquery';
import _ from 'lodash';
import FullScreen from '@/lib/fullscreen.js';
import pauseIframePlayers from '@/lib/slideshowIframeVideo';
import pjax from '@/vendor/pjax/pjax';
import Swipe from 'swipejs';
import VideoPlayer from '@/domain/services/VideoPlayer/VideoPlayer';
import VideoThumbnails from './video-thumbnails.js';
import { getUrlParameter, setUrlParameter } from '@/lib/url';

/**
 * Slideshow.
 *
 * Usage:
 *   const slideshow = new Slideshow();
 *   slideshow.setup(container);
 *
 */
export default class Slideshow {
	constructor() {
		this.slideshow = null;
		this.slider = null;
		this.slides = [];

		this.notFoundTemplate = _.template('<div class="no-slides-found"><h1><%= message %></h1></div>');

		this.selectors = {
			pjaxContainer: '#pjaxContainer',
			slideshow: '#slideshow',
			contactSheet: '#contact-sheet',
			slides: '.slideshow-slide, .slideshow-contact-sheet',
			video: 'video-js-standalone',
			youtube: 'iframe[id*=youtube]',
			vimeo: 'iframe[id*=vimeo]',
			videoThumbnails: 'video-link',
			pageTitle: 'div[data-title]',
			nav: {
				previous: '.slideshow-nav-previous',
				next: '.slideshow-nav-next',
				fullscreen: '.slideshow-nav-fullscreen',
			},
		};

		this.data = {
			previousEntry: 'previous-entry',
			nextEntry: 'next-entry',
			noSlidesFound: 'no-slides-found',
			pageTitle: 'title',
		};

		this.pjaxOptions = {
			container: this.selectors.pjaxContainer,
			timeout: 25000,
		};

		this.sliderOptions = {
			draggable: false,
			autoRestart: false,
			continuous: false,
			disableScroll: true,
			stopPropagation: true,
		};
	}

	_setupVideo() {
		this._setupStandaloneVideo();
		this._setupVideoThumbnails();
	}

	_setupStandaloneVideo() {
		VideoPlayer.setup(this.selectors.video, {
			fluid: false,
		});
	}

	_setupVideoThumbnails() {
		const videoThumbnails = new VideoThumbnails();
		videoThumbnails.setup(this.selectors.videoThumbnails);
	}

	_trackSlides() {
		this.slideshow.find(this.selectors.slides).each((index, slide) => {
			this.slides[index] = $(slide).attr('id');
		});
	}

	_setupSlider() {
		const startSlide = getUrlParameter(window.location.href, 'slideshow-reverse') ? this.slides.length - 1 : 0;

		this.slider = new Swipe(this.slideshow.find(this.selectors.slideshow)[0], { ...this.sliderOptions, startSlide });
	}

	_pauseVideoPlayback() {
		VideoPlayer.pause(this.selectors.video);
		pauseIframePlayers();
	}

	slideCount() {
		return this.slider.getNumSlides();
	}

	slideshowEmpty() {
		return this.slideCount() <= 1;
	}

	onFirstSlide() {
		return this.slider.getPos() === 0;
	}

	currentSlide() {
		return this.slider.getPos();
	}

	onLastSlide() {
		return this.slider.getNumSlides() === this.slider.getPos() + 1;
	}

	_entryUrl(direction, entryUrl) {
		// Start from the last slide when moving to the previous entry
		if (direction === 'previous') {
			entryUrl = setUrlParameter(entryUrl, 'slideshow-reverse', true);
		}

		return entryUrl;
	}

	previousEntry() {
		const urlVal = this.slideshow.data(this.data.previousEntry);
		return urlVal ? this._entryUrl('previous', urlVal) : '';
	}

	nextEntry() {
		const urlVal = this.slideshow.data(this.data.nextEntry);
		return urlVal ? this._entryUrl('next', urlVal) : '';
	}

	navigate(url) {
		if ($.support.pjax) {
			pjax.navigate({ ...this.pjaxOptions, url });
		} else {
			window.location.href = url;
		}
	}

	previousSlide() {
		const previousEntry = this.previousEntry();

		this._pauseVideoPlayback();

		if (this.onFirstSlide() && previousEntry) {
			this.navigate(previousEntry);
		} else {
			this.slider.prev();
			this._refreshNavigation();
		}
	}

	nextSlide() {
		const nextEntry = this.nextEntry();

		this._pauseVideoPlayback();

		if (this.onLastSlide() && nextEntry) {
			this.navigate(nextEntry);
		} else {
			this.slider.next();
			this._refreshNavigation();
		}
	}

	_refreshNavigation() {
		const nextButton = $(this.selectors.nav.next);
		const previousButton = $(this.selectors.nav.previous);

		nextButton.removeClass('hidden');
		previousButton.removeClass('hidden');

		if (this.onLastSlide() && !this.nextEntry()) {
			nextButton.addClass('hidden');
		}

		if (this.onFirstSlide() && !this.previousEntry()) {
			previousButton.addClass('hidden');
		}
	}

	slide(index) {
		this._pauseVideoPlayback();
		this.slider.slide(index);
		this._refreshNavigation();
	}

	_setupNavigation() {
		const previousButton = this.slideshow.find(this.selectors.nav.previous);
		const nextButton = this.slideshow.find(this.selectors.nav.next);

		previousButton.on('click.slideshow', (event) => {
			event.preventDefault();
			this.previousSlide();
		});

		nextButton.on('click.slideshow', (event) => {
			event.preventDefault();
			this.nextSlide();
		});

		this._refreshNavigation();
	}

	_setupKeyboardNavigation() {
		if ($.support.pjax) {
			$('body').off('keydown.slideshow');
		}

		$('body').on('keydown.slideshow', (event) => {
			// Left arrow
			if (event.which === 37) {
				this.previousSlide();
			}

			// Right arrow
			if (event.which === 39) {
				this.nextSlide();
			}
		});
	}

	_setupContactSheet() {
		$(this.selectors.contactSheet)
			.find('a')
			.on('click.slideshow', (event) => {
				const id = $(event.currentTarget).data('navigate-to');

				if (id) {
					this.slide(_.indexOf(this.slides, id));
				}
			});
	}

	_setupFullscreenMode() {
		const button = this.slideshow.find(this.selectors.nav.fullscreen);
		const fullscreen = new FullScreen();

		fullscreen.setup(button);
	}

	_showNotFoundMessage() {
		this.slideshow.find(this.selectors.contactSheet).html(
			this.notFoundTemplate({
				message: this.slideshow.data(this.data.noSlidesFound),
			})
		);
	}

	_pageTitleCallback() {
		const newTitle = $(document).find(this.selectors.pageTitle).first();

		if (newTitle) {
			document.title = newTitle.data(this.data.pageTitle);
		}
	}

	_setupPjax() {
		if (!$.support.pjax) {
			return;
		}

		$(document).on('pjax:end', this._pageTitleCallback.bind(this));
	}

	setup(container) {
		this.slideshow = $(container);

		if (!this.slideshow.length) {
			return;
		}

		this._trackSlides();
		this._setupVideo();
		this._setupSlider();
		this._setupNavigation();
		this._setupKeyboardNavigation();
		this._setupContactSheet();
		this._setupFullscreenMode();
		this._setupPjax();

		if (this.slideshowEmpty()) {
			this._showNotFoundMessage();
		}
	}
}
